/**
 * P1.1: Email Template Context (Updated to use Email Event Dictionary)
 * Manages email templates for all lifecycle events (MOCKED - no real email sending)
 * 
 * BACKEND-READY ARCHITECTURE:
 * - Templates are driven by EMAIL_EVENTS dictionary (immutable event keys)
 * - Admins can only edit content (subject/body), not event keys
 * - Compatible with future JWT auth + email service integration
 * 
 * @version 2.0.0
 * @lastUpdated 2026-01-26
 */
import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import {
  EMAIL_EVENTS,
  EMAIL_AUDIENCE,
  TEMPLATE_VARIABLES,
  getEventsByAudience,
  getUserEditableEvents,
  getEventByKey,
  getEventVariables,
  getEventStatistics,
  TEMPLATE_ID_TO_EVENT_KEY_MAP,
} from '../config/emailEventDictionary';
import api from '../utils/api';

const EmailTemplateContext = createContext();

export const useEmailTemplates = () => {
  const context = useContext(EmailTemplateContext);
  if (!context) {
    throw new Error('useEmailTemplates must be used within EmailTemplateProvider');
  }
  return context;
};

// Re-export from dictionary for backward compatibility
export { EMAIL_AUDIENCE, TEMPLATE_VARIABLES };

// Legacy trigger mapping (for backward compatibility with existing UI)
export const EMAIL_TRIGGERS = {
  APPLICATION_SUBMITTED: 'CLINIC_APPLICATION_SUBMITTED',
  APPLICATION_APPROVED: 'CLINIC_APPLICATION_APPROVED',
  APPLICATION_REJECTED: 'CLINIC_APPLICATION_REJECTED',
  APPLICATION_REAPPLIED: 'CLINIC_APPLICATION_REAPPLIED',
  TRIAL_STARTED: 'CLINIC_TRIAL_STARTED',
  TRIAL_ENDING_SOON: 'CLINIC_TRIAL_ENDING_SOON',
  TRIAL_EXPIRED: 'CLINIC_TRIAL_EXPIRED',
  PLAN_UPGRADED: 'CLINIC_PLAN_UPGRADED',
};

// =============================================================================
// DEFAULT TEMPLATE CONTENT
// =============================================================================
const DEFAULT_TEMPLATE_CONTENT = {
  // Clinic Application Templates
  CLINIC_APPLICATION_SUBMITTED: {
    subject: 'Your TreatX Application Has Been Received - {{clinicName}}',
    body: `Dear {{clinicName}} Team,

Thank you for submitting your clinic registration application to TreatX!

**Application Details:**
- Application ID: {{applicationId}}
- Clinic Name: {{clinicName}}
- Email: {{clinicEmail}}

**What's Next?**
Our team will review your application within 1-2 business days. You will receive an email notification once your application has been processed.

You can check your application status at any time by visiting our platform and entering your registered email address.

If you have any questions, please contact us at {{supportEmail}}.

Welcome to TreatX!

Best regards,
The TreatX Team`,
  },
  
  CLINIC_APPLICATION_APPROVED: {
    subject: '🎉 Congratulations! Your TreatX Application is Approved - {{clinicName}}',
    body: `Dear {{clinicName}} Team,

Great news! Your clinic registration application has been approved!

**Application Details:**
- Application ID: {{applicationId}}
- Reviewed By: {{reviewerName}}
- Review Date: {{reviewDate}}

**Next Steps:**
1. Log in to your TreatX dashboard
2. Choose to start your FREE 14-day trial or select a paid plan
3. Set up your clinic profile and start receiving patient inquiries

**Getting Started:**
Visit {{platformUrl}} and enter your registered email to access your dashboard.

If you have any questions about getting started, our team is here to help at {{supportEmail}}.

Welcome to the TreatX family!

Best regards,
The TreatX Team`,
  },
  
  CLINIC_APPLICATION_REJECTED: {
    subject: 'Action Required: Your TreatX Application Needs Attention - {{clinicName}}',
    body: `Dear {{clinicName}} Team,

We have reviewed your clinic registration application and unfortunately, we were unable to approve it at this time.

**Application Details:**
- Application ID: {{applicationId}}
- Reviewed By: {{reviewerName}}
- Review Date: {{reviewDate}}

**Reason for Rejection:**
{{rejectionReason}}

**Fields Requiring Correction:**
{{flaggedFields}}

**What You Can Do:**
You can reapply by visiting our platform and making the necessary corrections to the flagged fields. Once updated, submit your application for another review.

Visit {{platformUrl}} and check your application status to begin the reapplication process.

If you believe this decision was made in error or have questions, please contact us at {{supportEmail}}.

Best regards,
The TreatX Team`,
  },
  
  CLINIC_APPLICATION_REAPPLIED: {
    subject: 'Reapplication Received - {{clinicName}}',
    body: `Dear {{clinicName}} Team,

We have received your corrected application and it is now under review.

**Application Details:**
- Application ID: {{applicationId}}
- Status: Under Review

Thank you for taking the time to address the requested corrections. Our team will review your updated information within 1-2 business days.

You will receive an email notification once your reapplication has been processed.

If you have any questions, please contact us at {{supportEmail}}.

Best regards,
The TreatX Team`,
  },
  
  // Trial Templates
  CLINIC_TRIAL_STARTED: {
    subject: '🚀 Your TreatX Free Trial Has Started - {{clinicName}}',
    body: `Dear {{clinicName}} Team,

Your 14-day free trial has officially started! 

**Trial Details:**
- Start Date: {{trialStartDate}}
- End Date: {{trialEndDate}}
- Plan: Free Trial

**What's Included:**
✓ Up to 5 leads per month
✓ 1 sales representative account
✓ Basic CRM features
✓ Patient inquiry management

**Getting Started Tips:**
1. Complete your clinic profile
2. Add your treatments and pricing
3. Start responding to patient inquiries
4. Explore the dashboard and reports

**Need Help?**
Visit our help center or contact us at {{supportEmail}}.

Make the most of your trial!

Best regards,
The TreatX Team`,
  },
  
  CLINIC_TRIAL_ENDING_SOON: {
    subject: '⏰ Your TreatX Trial Ends in {{trialDaysRemaining}} Days - {{clinicName}}',
    body: `Dear {{clinicName}} Team,

Your free trial is ending soon!

**Trial Status:**
- Days Remaining: {{trialDaysRemaining}}
- End Date: {{trialEndDate}}

**Don't Lose Your Progress!**
Upgrade to a paid plan to:
✓ Keep all your data and settings
✓ Unlock unlimited features
✓ Continue receiving patient inquiries
✓ Access advanced analytics

**Upgrade Options:**
- Starter: €49/month - For small clinics
- Professional: €99/month - For growing clinics
- Enterprise: Custom pricing - For large organizations

Visit {{platformUrl}} to upgrade your plan before your trial expires.

Questions? Contact us at {{supportEmail}}.

Best regards,
The TreatX Team`,
  },
  
  CLINIC_TRIAL_EXPIRED: {
    subject: '⚠️ Your TreatX Trial Has Expired - {{clinicName}}',
    body: `Dear {{clinicName}} Team,

Your free trial has expired.

**What This Means:**
- Your account is now in read-only mode
- You cannot receive new patient inquiries
- Your existing data is preserved for 30 days

**Reactivate Your Account:**
Upgrade to any paid plan to immediately restore full access to your account and all your data.

**Available Plans:**
- Starter: €49/month
- Professional: €99/month  
- Enterprise: Custom pricing

Visit {{platformUrl}} to upgrade and continue growing your medical tourism business.

**Need More Time?**
Contact us at {{supportEmail}} to discuss your options.

We hope to see you back soon!

Best regards,
The TreatX Team`,
  },
  
  // Subscription Templates
  CLINIC_PLAN_UPGRADED: {
    subject: '✨ Welcome to {{planName}} - {{clinicName}}',
    body: `Dear {{clinicName}} Team,

Thank you for upgrading to {{planName}}!

**Upgrade Details:**
- Previous Plan: {{previousPlan}}
- New Plan: {{planName}}
- Price: {{planPrice}}

**Your New Features:**
Your upgraded plan includes expanded limits and premium features. Log in to your dashboard to explore everything that's now available to you.

**What's New:**
✓ Increased lead limits
✓ More team member accounts
✓ Advanced analytics and reporting
✓ Priority support

If you have any questions about your new plan or need help getting started with any features, our team is here to help at {{supportEmail}}.

Thank you for choosing TreatX!

Best regards,
The TreatX Team`,
  },
  
  // Patient Authentication Templates
  PATIENT_ACCOUNT_CREATED: {
    subject: 'Welcome to TreatX - Verify Your Email',
    body: `Dear {{patientFirstName}},

Welcome to TreatX! Your account has been created successfully.

Please verify your email address to get started:
{{verificationLink}}

**Your Account:**
- Email: {{patientEmail}}

Once verified, you can:
✓ Submit your treatment inquiry
✓ Receive personalized clinic matches
✓ Compare offers from top clinics
✓ Book your medical journey

If you have any questions, contact us at {{supportEmail}}.

Best regards,
The TreatX Team`,
  },
  
  PATIENT_EMAIL_VERIFICATION: {
    subject: 'Verify Your Email - TreatX',
    body: `Dear {{patientFirstName}},

Please verify your email address by clicking the link below:
{{verificationLink}}

Or enter this verification code: {{verificationCode}}

This link expires in 24 hours.

If you didn't create a TreatX account, please ignore this email.

Best regards,
The TreatX Team`,
  },
  
  PATIENT_PASSWORD_RESET_REQUEST: {
    subject: 'Reset Your TreatX Password',
    body: `Dear {{patientFirstName}},

We received a request to reset your password.

Click the link below to create a new password:
{{resetPasswordLink}}

This link expires in 1 hour.

If you didn't request a password reset, please ignore this email or contact us at {{supportEmail}} if you have concerns.

Best regards,
The TreatX Team`,
  },
  
  PATIENT_PASSWORD_CHANGED: {
    subject: 'Your TreatX Password Has Been Changed',
    body: `Dear {{patientFirstName}},

Your password was successfully changed on {{currentDate}}.

If you made this change, no further action is needed.

If you did NOT change your password, please contact us immediately at {{supportEmail}}.

Best regards,
The TreatX Team`,
  },
  
  // Patient Intake Templates
  PATIENT_INTAKE_SUBMITTED: {
    subject: 'We Received Your Treatment Inquiry - TreatX',
    body: `Dear {{patientFirstName}},

Thank you for submitting your treatment inquiry!

**Inquiry Details:**
- Inquiry ID: {{intakeId}}
- Treatment: {{treatmentType}}
- Preferred Destination: {{preferredDestination}}

**What Happens Next:**
1. Our team reviews your information
2. We match you with qualified clinics
3. You receive personalized offers
4. You compare and choose the best option

We'll notify you as soon as we have matches. This typically takes 24-48 hours.

Track your inquiry at {{platformUrl}}.

Questions? Contact us at {{supportEmail}}.

Best regards,
The TreatX Team`,
  },
  
  PATIENT_INTAKE_UPDATED: {
    subject: 'Your Treatment Inquiry Has Been Updated - TreatX',
    body: `Dear {{patientFirstName}},

Your treatment inquiry ({{intakeId}}) has been successfully updated.

We'll review your updated information and continue matching you with the best clinics.

View your inquiry at {{platformUrl}}.

Best regards,
The TreatX Team`,
  },
  
  // Patient Matching Templates
  PATIENT_MATCHING_STARTED: {
    subject: 'We\'re Finding Your Perfect Clinic Match - TreatX',
    body: `Dear {{patientFirstName}},

Great news! We've started searching for the best clinics for your {{treatmentType}} treatment.

Our matching process considers:
✓ Your treatment requirements
✓ Budget preferences
✓ Location preferences
✓ Clinic ratings and reviews

We'll notify you as soon as we find matches.

Track progress at {{platformUrl}}.

Best regards,
The TreatX Team`,
  },
  
  PATIENT_MATCHES_FOUND: {
    subject: '🎉 {{matchCount}} Clinics Match Your Criteria - TreatX',
    body: `Dear {{patientFirstName}},

Great news! We found {{matchCount}} clinics that match your {{treatmentType}} requirements!

**Matched Clinics:**
{{matchedClinics}}

**Next Steps:**
1. Review each clinic's profile
2. Compare offers and pricing
3. Ask questions directly
4. Choose your preferred clinic

View your matches at {{platformUrl}}.

Questions? Contact us at {{supportEmail}}.

Best regards,
The TreatX Team`,
  },
  
  PATIENT_NO_MATCH_FOUND: {
    subject: 'Update on Your Treatment Inquiry - TreatX',
    body: `Dear {{patientFirstName}},

We've searched our network for {{treatmentType}} clinics in {{preferredDestination}}, but we haven't found an exact match for your specific requirements yet.

**What You Can Do:**
1. Expand your location preferences
2. Adjust your budget range
3. Consider alternative treatment options

Update your preferences at {{platformUrl}}.

Our team is also actively recruiting new clinics. We'll notify you if a suitable match becomes available.

Questions? Contact us at {{supportEmail}}.

Best regards,
The TreatX Team`,
  },
  
  // Patient Offer Templates
  PATIENT_OFFER_RECEIVED: {
    subject: '💼 New Offer from {{clinicName}} - TreatX',
    body: `Dear {{patientFirstName}},

You've received a new treatment offer!

**Offer Details:**
- Offer ID: {{offerId}}
- Clinic: {{clinicName}}
- Treatment: {{treatmentType}}
- Amount: {{offerAmount}} {{offerCurrency}}
- Valid Until: {{offerExpiry}}

**What's Included:**
Review the complete offer to see included services, accommodation, and travel assistance.

View and respond to this offer at {{platformUrl}}.

Questions? Contact us at {{supportEmail}}.

Best regards,
The TreatX Team`,
  },
  
  PATIENT_OFFER_REVISED: {
    subject: '📝 {{clinicName}} Has Updated Their Offer - TreatX',
    body: `Dear {{patientFirstName}},

{{clinicName}} has revised their offer for you.

**Updated Offer:**
- Offer ID: {{offerId}}
- New Amount: {{offerAmount}} {{offerCurrency}}
- Valid Until: {{offerExpiry}}

Review the updated offer at {{platformUrl}}.

Best regards,
The TreatX Team`,
  },
  
  PATIENT_OFFER_ACCEPTED: {
    subject: '✅ Offer Accepted - Your Treatment Journey Begins!',
    body: `Dear {{patientFirstName}},

Congratulations! You've accepted the offer from {{clinicName}}.

**Accepted Offer:**
- Offer ID: {{offerId}}
- Treatment: {{treatmentType}}
- Amount: {{offerAmount}}

**Next Steps:**
1. The clinic will contact you to schedule your treatment
2. You may be asked to pay a deposit
3. Start planning your travel

Manage your booking at {{platformUrl}}.

Questions? Contact us at {{supportEmail}}.

Best regards,
The TreatX Team`,
  },
  
  // Patient Payment Templates
  PATIENT_DEPOSIT_REQUESTED: {
    subject: '💳 Deposit Requested - {{clinicName}}',
    body: `Dear {{patientFirstName}},

{{clinicName}} has requested a deposit to secure your treatment booking.

**Deposit Details:**
- Amount: {{depositAmount}}
- Due Date: {{depositDueDate}}

**Payment Options:**
Log in to your dashboard to view secure payment options.

Pay your deposit at {{platformUrl}}.

Questions? Contact us at {{supportEmail}}.

Best regards,
The TreatX Team`,
  },
  
  PATIENT_DEPOSIT_RECEIVED: {
    subject: '✅ Deposit Confirmed - {{clinicName}}',
    body: `Dear {{patientFirstName}},

Your deposit has been received and confirmed!

**Payment Details:**
- Amount: {{depositAmount}}
- Payment Method: {{paymentMethod}}
- Clinic: {{clinicName}}

Your treatment booking is now secured. The clinic will contact you with next steps.

View your booking at {{platformUrl}}.

Best regards,
The TreatX Team`,
  },
  
  // Internal Templates (not shown in admin UI)
  ADMIN_NEW_HIGH_VALUE_PATIENT: {
    subject: '[HIGH VALUE] New Patient Lead - {{patientName}}',
    body: `New high-value patient registered.

**Patient Details:**
- Name: {{patientName}}
- Email: {{patientEmail}}
- Country: {{patientCountry}}
- Treatment: {{treatmentType}}

**Lead Score:** {{leadScore}}
**Estimated Value:** {{estimatedValue}}

This lead requires priority handling.`,
  },
  
  ADMIN_MULTIPLE_REJECTION_ALERT: {
    subject: '[ALERT] Multiple Application Rejections',
    body: `Alert: Multiple application rejections detected.

**Details:**
- Reason: {{alertReason}}
- Reviewer: {{reviewerName}}
- Date: {{currentDate}}

Please review for potential issues.`,
  },
  
  SALES_REP_ASSIGNED_TO_LEAD: {
    subject: 'New Lead Assigned - {{patientName}}',
    body: `Hello {{salesRepName}},

You've been assigned a new lead.

**Lead Details:**
- Patient: {{patientName}}
- Treatment: {{treatmentType}}
- Lead Score: {{leadScore}}
- Clinic: {{clinicName}}

Please follow up within 24 hours.

Best regards,
TreatX System`,
  },
};

// =============================================================================
// BUILD TEMPLATES FROM DICTIONARY
// =============================================================================

/**
 * Generate template objects from event dictionary
 */
const buildTemplatesFromDictionary = () => {
  return Object.entries(EMAIL_EVENTS).map(([eventKey, eventConfig]) => {
    const content = DEFAULT_TEMPLATE_CONTENT[eventKey] || { subject: '', body: '' };
    
    return {
      id: `tpl_${eventKey.toLowerCase()}`,
      eventKey: eventKey,
      name: eventConfig.displayName,
      trigger: eventKey, // Use event key as trigger
      audience: eventConfig.audience,
      category: eventConfig.category,
      triggerSource: eventConfig.triggerSource,
      description: eventConfig.description,
      subject: content.subject,
      body: content.body,
      defaultVariables: eventConfig.defaultVariables,
      isActive: eventConfig.isActiveByDefault,
      isUserEditable: eventConfig.isUserEditable,
      isDefault: true,
      priority: eventConfig.priority,
      lastModified: null,
      modifiedBy: null,
    };
  });
};

const DEFAULT_TEMPLATES = buildTemplatesFromDictionary();

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export const EmailTemplateProvider = ({ children }) => {
  const [templates, setTemplates] = useState(DEFAULT_TEMPLATES);
  const [loading, setLoading] = useState(true);
  // TREATX-OWNER-UAT-EMAIL-EN-TR-01 (2026-08): the canonical
  // `email_templates` collection stores one row per
  // `(event_key, locale)`. The admin UI is now locale-aware: the
  // active locale drives both the fetch (via `?locale=` query) and
  // the save (via the request body's `locale` field). Default 'en'
  // preserves backward-compatible UX for existing admins.
  const [selectedLocale, setSelectedLocale] = useState('en');

  // Load templates from backend for the currently-selected locale,
  // merged with the defaults dictionary.
  useEffect(() => {
    let cancelled = false;
    const loadFromBackend = async () => {
      setLoading(true);
      try {
        const response = await api.get(`/email-templates?locale=${encodeURIComponent(selectedLocale)}`);
        const data = response.data;
        if (cancelled) return;
        if (data.success && data.templates && data.templates.length > 0) {
          // Merge backend data with defaults; the backend response is
          // already single-locale (filtered by `?locale=`), so
          // `.find()` is deterministic across event_keys.
          const merged = DEFAULT_TEMPLATES.map(defaultTpl => {
            const saved = data.templates.find(t => t.event_key === defaultTpl.eventKey);
            if (saved) {
              return {
                ...defaultTpl,
                subject: saved.subject || defaultTpl.subject,
                body: saved.body || defaultTpl.body,
                isActive: saved.is_active !== undefined ? saved.is_active : defaultTpl.isActive,
                isDefault: saved.is_default !== undefined ? saved.is_default : defaultTpl.isDefault,
                lastModified: saved.last_modified || defaultTpl.lastModified,
                modifiedBy: saved.modified_by || defaultTpl.modifiedBy,
                locale: saved.locale || selectedLocale,
                localeHasRow: true,
              };
            }
            return { ...defaultTpl, locale: selectedLocale, localeHasRow: false };
          });
          setTemplates(merged);
        } else {
          // No rows for this locale — reset to defaults marked as absent.
          setTemplates(DEFAULT_TEMPLATES.map(t => ({ ...t, locale: selectedLocale, localeHasRow: false })));
        }
      } catch (err) {
        if (!cancelled) console.error('[EmailTemplates] Failed to load from backend:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    loadFromBackend();
    return () => { cancelled = true; };
  }, [selectedLocale]);

  // Save a single template to backend, pinned to the currently-selected locale.
  const saveToBackend = async (eventKey, updates) => {
    try {
      await api.put(`/email-templates/${eventKey}`, { ...updates, locale: selectedLocale });
    } catch (err) {
      console.error('[EmailTemplates] Failed to save to backend:', err);
    }
  };

  /**
   * Get all templates
   */
  const getAllTemplates = useCallback(() => {
    return templates;
  }, [templates]);

  /**
   * Get templates by audience (for filtered views)
   */
  const getTemplatesByAudience = useCallback((audience) => {
    return templates.filter(t => t.audience === audience);
  }, [templates]);

  /**
   * Get only user-editable templates (excludes internal)
   */
  const getEditableTemplates = useCallback(() => {
    return templates.filter(t => t.isUserEditable);
  }, [templates]);

  /**
   * Get template by ID
   */
  const getTemplateById = useCallback((templateId) => {
    return templates.find(t => t.id === templateId);
  }, [templates]);

  /**
   * Get template by event key
   */
  const getTemplateByEventKey = useCallback((eventKey) => {
    return templates.find(t => t.eventKey === eventKey);
  }, [templates]);

  /**
   * Get template by trigger (legacy support)
   */
  const getTemplateByTrigger = useCallback((trigger) => {
    return templates.find(t => t.trigger === trigger || t.eventKey === trigger);
  }, [templates]);

  /**
   * Update template content (subject/body only)
   */
  const updateTemplate = useCallback((templateId, updates, adminName = 'TreatX Admin') => {
    setTemplates(prev => prev.map(tpl => {
      if (tpl.id === templateId) {
        const updated = {
          ...tpl,
          subject: updates.subject ?? tpl.subject,
          body: updates.body ?? tpl.body,
          isDefault: false,
          lastModified: new Date().toISOString(),
          modifiedBy: adminName,
        };
        // Persist to backend
        saveToBackend(tpl.eventKey, {
          subject: updated.subject,
          body: updated.body,
          is_active: updated.isActive,
          is_default: false,
          modified_by: adminName,
        });
        return updated;
      }
      return tpl;
    }));
  }, []);

  /**
   * Toggle template active status
   */
  const toggleTemplateStatus = useCallback((templateId) => {
    setTemplates(prev => prev.map(tpl => {
      if (tpl.id === templateId) {
        const updated = {
          ...tpl,
          isActive: !tpl.isActive,
          lastModified: new Date().toISOString(),
        };
        saveToBackend(tpl.eventKey, { is_active: updated.isActive });
        return updated;
      }
      return tpl;
    }));
  }, []);

  /**
   * Reset template to default content
   */
  const resetToDefault = useCallback((templateId) => {
    const template = templates.find(t => t.id === templateId);
    if (!template) return;
    
    const defaultContent = DEFAULT_TEMPLATE_CONTENT[template.eventKey];
    if (defaultContent) {
      setTemplates(prev => prev.map(tpl => {
        if (tpl.id === templateId) {
          saveToBackend(tpl.eventKey, {
            subject: defaultContent.subject,
            body: defaultContent.body,
            is_default: true,
            modified_by: null,
          });
          return {
            ...tpl,
            subject: defaultContent.subject,
            body: defaultContent.body,
            isDefault: true,
            lastModified: null,
            modifiedBy: null,
          };
        }
        return tpl;
      }));
    }
  }, [templates]);

  /**
   * Generate preview with mock data
   */
  const generatePreview = useCallback((template, customData = {}) => {
    // Build mock data from TEMPLATE_VARIABLES
    const mockData = {};
    Object.entries(TEMPLATE_VARIABLES).forEach(([key, config]) => {
      mockData[key] = config.example;
    });
    
    // Override with custom data
    Object.assign(mockData, customData);

    let previewSubject = template.subject;
    let previewBody = template.body;

    // Replace all variables
    Object.entries(mockData).forEach(([key, value]) => {
      const regex = new RegExp(`{{${key}}}`, 'g');
      previewSubject = previewSubject.replace(regex, value);
      previewBody = previewBody.replace(regex, value);
    });

    return {
      subject: previewSubject,
      body: previewBody,
    };
  }, []);

  /**
   * Get variables used in a template
   */
  const getUsedVariables = useCallback((template) => {
    const content = template.subject + template.body;
    const usedVars = [];
    
    Object.keys(TEMPLATE_VARIABLES).forEach(varName => {
      if (content.includes(`{{${varName}}}`)) {
        usedVars.push(varName);
      }
    });
    
    return usedVars;
  }, []);

  /**
   * Get statistics for dashboard
   */
  const getStatistics = useCallback(() => {
    const stats = getEventStatistics();
    const activeCount = templates.filter(t => t.isActive).length;
    const customizedCount = templates.filter(t => !t.isDefault).length;
    
    return {
      ...stats,
      active: activeCount,
      customized: customizedCount,
    };
  }, [templates]);

  const value = {
    templates,
    loading,
    // TREATX-OWNER-UAT-EMAIL-EN-TR-01 — expose locale state for the
    // admin UI toggle. Consumers switch by calling `setSelectedLocale`.
    selectedLocale,
    setSelectedLocale,
    getAllTemplates,
    getTemplatesByAudience,
    getEditableTemplates,
    getTemplateById,
    getTemplateByEventKey,
    getTemplateByTrigger,
    updateTemplate,
    toggleTemplateStatus,
    resetToDefault,
    generatePreview,
    getUsedVariables,
    getStatistics,
    // Re-exports from dictionary
    EMAIL_EVENTS,
    EMAIL_AUDIENCE,
    TEMPLATE_VARIABLES,
    EMAIL_TRIGGERS,
  };

  return (
    <EmailTemplateContext.Provider value={value}>
      {children}
    </EmailTemplateContext.Provider>
  );
};

export default EmailTemplateContext;
